import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import { EmptyState, ParentPageHeader, PortalLoading, StatusBadge, niceDateTime, useParentPortal } from "./parentPortalUtils";

const initialForm = { student_id: "", category: "", subject: "", message: "", priority: "NORMAL" };

export default function ParentReportsPage() {
  const { data, loading, reload } = useParentPortal("parent-portal/reports/");
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const reports = data?.reports || [];
  const children = data?.children || [];
  const selectedChild = useMemo(() => children.find((child) => String(child.id) === String(form.student_id)), [children, form.student_id]);

  const submit = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      const response = await axiosInstance.post("parent-portal/reports/", form);
      toast.success(response.data?.message || "Report sent successfully.");
      setForm(initialForm);
      reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send the report.");
    } finally {
      setSaving(false);
    }
  };

  if (loading && !data) return <PortalLoading />;

  return (
    <div className="container-fluid py-4">
      <ParentPageHeader title="Parent Reports & Communication" subtitle="Privately share behaviour, health, academic, attendance or welfare information with your child’s class teacher." icon="bi-chat-left-text" actions={<button className="btn btn-light border" onClick={reload}>Refresh</button>} />

      <div className="row g-3">
        <div className="col-xl-5">
          <div className="card border-0 shadow-sm">
            <div className="card-body p-4">
              <h5 className="fw-bold mb-1">Send a report</h5>
              <p className="text-muted small">This is a private school communication. It is not posted to the whole class.</p>
              <form onSubmit={submit} className="row g-3">
                <div className="col-12"><label className="form-label fw-semibold">Child</label><select className="form-select" value={form.student_id} onChange={(e) => setForm({ ...form, student_id: e.target.value })} required><option value="">Select child</option>{children.map((child) => <option key={child.id} value={child.id}>{child.full_name} {child.class_group?.name ? `— ${child.class_group.name}` : ""}</option>)}</select></div>
                {selectedChild && <div className="col-12"><div className="alert alert-light border mb-0 small"><strong>Class teacher:</strong> {selectedChild.class_group?.class_teacher || "Not assigned yet"}</div></div>}
                <div className="col-md-7"><label className="form-label fw-semibold">Category</label><select className="form-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} required><option value="">Select category</option><option value="BEHAVIOUR">Behaviour at home</option><option value="HEALTH">Health or allergy update</option><option value="ACADEMIC">Academic concern</option><option value="ATTENDANCE">Attendance information</option><option value="WELFARE">Family or welfare matter</option><option value="GENERAL">General message</option></select></div>
                <div className="col-md-5"><label className="form-label fw-semibold">Priority</label><select className="form-select" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}><option value="NORMAL">Normal</option><option value="IMPORTANT">Important</option><option value="URGENT">Urgent</option></select></div>
                <div className="col-12"><label className="form-label fw-semibold">Subject</label><input className="form-control" maxLength="180" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required /></div>
                <div className="col-12"><label className="form-label fw-semibold">What should the teacher know?</label><textarea className="form-control" rows="5" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required /></div>
                <div className="col-12"><button className="btn btn-success w-100" disabled={saving || !children.length}>{saving ? "Sending..." : "Send privately to class teacher"}</button></div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-xl-7">
          <div className="card border-0 shadow-sm">
            <div className="card-body p-4">
              <h5 className="fw-bold mb-3">Communication history</h5>
              {!reports.length ? <EmptyState icon="bi-chat-square-text" title="No reports sent" message="Reports and teacher responses will appear here." /> : reports.map((item) => <div className="border rounded-3 p-3 mb-3" key={item.id}>
                <div className="d-flex flex-wrap justify-content-between gap-2"><div><div className="fw-bold">{item.subject}</div><small className="text-muted">{item.student?.full_name} · {item.category_label} · {niceDateTime(item.created_at)}</small></div><div className="d-flex gap-2"><StatusBadge value={item.priority} /><StatusBadge value={item.status_label} /></div></div>
                <p className="mb-2 mt-3">{item.message}</p>
                <div className="small text-muted">Sent to: {item.class_teacher}</div>
                {item.teacher_response && <div className="alert alert-success mt-3 mb-0"><div className="fw-semibold mb-1">Teacher response</div>{item.teacher_response}</div>}
              </div>)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
